import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import PublicPageShell from '../components/layout/PublicPageShell';
import { db } from '../firebase';
import { getMemberFirstName } from '../utils/memberDisplayName';
import {
  ClinicalHistorySlot,
  createClinicalHistoryBooking,
  listAvailableClinicalHistorySlots,
  SlotUnavailableError,
} from '../services/clinicalHistoryService';
import {
  formatLondonDayOfMonth,
  formatLondonLongDay,
  formatLondonShortMonth,
  formatLondonTime,
  formatLondonWeekday,
  londonDateKey,
} from '../utils/londonTime';

const ClinicalHistoryBookPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [slots, setSlots] = useState<ClinicalHistorySlot[]>([]);
  // Value intentionally discarded: the name is captured but nothing renders it
  // yet. Keeping the setter so the capture survives for whoever adds the read.
  const [, setMemberName] = useState('');
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(false);
  const [bookedSlot, setBookedSlot] = useState<ClinicalHistorySlot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!currentUser) return;
      try {
        const [availableSlots, userSnap] = await Promise.all([
          listAvailableClinicalHistorySlots(),
          getDoc(doc(db, 'users', currentUser.uid)),
        ]);
        setSlots(availableSlots);
        const userData = userSnap.exists() ? userSnap.data() : {};
        const first = getMemberFirstName({
          firstName: userData.firstName,
          displayName: userData.displayName || currentUser.displayName,
        });
        const fullName = [userData.firstName, userData.lastName].filter(Boolean).join(' ')
          || userData.displayName
          || currentUser.displayName
          || first;
        setMemberName(fullName);
      } catch (e) {
        console.error(e);
        setError('Could not load available slots.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  const handleBook = async (slotId: string) => {
    if (!currentUser) return;
    setError(null);
    setBooking(true);
    try {
      await createClinicalHistoryBooking({ slotId });
      setBookedSlot(slots.find((s) => s.id === slotId) ?? null);
      setBooked(true);
    } catch (e) {
      console.error(e);
      if (e instanceof SlotUnavailableError) {
        // Reachable now that bookings confirm instantly: two members really
        // can race for the same time. Refresh so they see what is still free.
        setError('Sorry — that time was just taken. Here are the times still available.');
        const refreshed = await listAvailableClinicalHistorySlots().catch(() => []);
        setSlots(refreshed);
      } else {
        setError('Booking failed. Please try again.');
      }
    } finally {
      setBooking(false);
    }
  };

  // Group available slots into calendar days, ordered chronologically.
  // Keyed on the LONDON date: grouping on the browser's local date put an
  // evening slot in the wrong day for anyone outside the UK.
  const dayGroups = useMemo(() => {
    const map = new Map<string, { key: string; date: Date; slots: ClinicalHistorySlot[] }>();
    for (const slot of slots) {
      const d = slot.startAt.toDate();
      const key = londonDateKey(d);
      if (!map.has(key)) map.set(key, { key, date: d, slots: [] });
      map.get(key)!.slots.push(slot);
    }
    const groups = Array.from(map.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
    groups.forEach((g) => g.slots.sort((a, b) => a.startAt.toDate().getTime() - b.startAt.toDate().getTime()));
    return groups;
  }, [slots]);

  useEffect(() => {
    if (!selectedDay && dayGroups.length > 0) {
      setSelectedDay(dayGroups[0].key);
    }
  }, [dayGroups, selectedDay]);

  const activeGroup = dayGroups.find((g) => g.key === selectedDay) ?? dayGroups[0];

  // Always London — these are London appointments, and rendering them in the
  // viewer's zone showed a member abroad the wrong time for their own call.
  const formatTime = (slot: ClinicalHistorySlot) => formatLondonTime(slot.startAt.toDate());

  const longDayLabel = (date: Date) => formatLondonLongDay(date);

  return (
    <PublicPageShell
      backHref="/dashboard"
      backLabel="Back to dashboard"
      contentClassName="max-w-2xl mx-auto"
      mainClassName="flex-1 px-6 md:px-12 pb-16"
    >
        <div className="max-w-xl mx-auto">
          <h1
            className="text-2xl font-normal mb-4"
            style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
          >
            Book your clinical history
          </h1>
          <p className="text-sm mb-6" style={{ color: 'var(--melio-text-muted)' }}>
            A doctor trained in taking medical and family history will conduct a video call with you. No forms to fill
            in — they will guide the conversation. We review everything together once we have your genetics, bloods, and
            this clinical history.
          </p>

          {booked ? (
            <div
              className="rounded-xl border p-6"
              style={{ borderColor: 'var(--melio-sage)', backgroundColor: 'rgba(79, 93, 24, 0.1)' }}
            >
              <p className="font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>
                You&apos;re booked
              </p>
              <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                {bookedSlot
                  ? `${formatLondonLongDay(bookedSlot.startAt.toDate())} at ${formatLondonTime(bookedSlot.startAt.toDate())}${bookedSlot.doctorName ? ` with ${bookedSlot.doctorName}` : ''}.`
                  : 'Your appointment is confirmed.'}
                {' '}The joining link and details are in your messages, and we&apos;ve emailed them to you.
              </p>
              <Link
                to="/dashboard"
                className="inline-block mt-4 text-sm font-medium"
                style={{ color: 'var(--melio-sage)' }}
              >
                Back to dashboard
              </Link>
            </div>
          ) : loading ? (
            <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
              Loading available slots...
            </p>
          ) : slots.length === 0 ? (
            <div
              className="rounded-xl border p-6"
              style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
            >
              <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                No slots available right now. Ops will add more soon — check back later or we&apos;ll email you when new
                times are released.
              </p>
              <Link
                to="/dashboard"
                className="inline-block mt-4 px-6 py-3 font-medium rounded transition-opacity hover:opacity-90"
                style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
              >
                Back to dashboard
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <p className="text-sm mb-4" style={{ color: '#b91c1c' }}>
                  {error}
                </p>
              )}

              {/* Day picker */}
              <p className="text-sm mb-3" style={{ color: 'var(--melio-text-muted)' }}>
                Choose a day:
              </p>
              <div className="flex gap-2 overflow-x-auto pb-2 mb-7">
                {dayGroups.map((group) => {
                  const selected = group.key === activeGroup?.key;
                  return (
                    <button
                      key={group.key}
                      type="button"
                      onClick={() => setSelectedDay(group.key)}
                      className={`cal-day flex-none flex flex-col items-center justify-center rounded-lg border py-3${selected ? ' is-selected' : ''}`}
                      style={{
                        minWidth: 78,
                        borderColor: selected ? '#3D0B0D' : 'var(--melio-border)',
                        backgroundColor: selected ? '#3D0B0D' : 'white',
                        color: selected ? '#F7F6F2' : 'var(--melio-charcoal)',
                      }}
                    >
                      <span className="text-[10px] uppercase tracking-[0.16em]" style={{ opacity: 0.75 }}>
                        {formatLondonWeekday(group.date)}
                      </span>
                      <span
                        className="text-2xl leading-none my-0.5"
                        style={{ fontFamily: "var(--melio-display)" }}
                      >
                        {formatLondonDayOfMonth(group.date)}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.16em]" style={{ opacity: 0.75 }}>
                        {formatLondonShortMonth(group.date)}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Time grid for the selected day */}
              {activeGroup && (
                <>
                  <p className="text-sm mb-3" style={{ color: 'var(--melio-text-muted)' }}>
                    Times for {longDayLabel(activeGroup.date)}:
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {activeGroup.slots.map((slot) => (
                      <button
                        key={slot.id}
                        type="button"
                        onClick={() => handleBook(slot.id)}
                        disabled={booking}
                        className="cal-slot text-left px-4 py-3 rounded-lg border disabled:opacity-50"
                        style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
                      >
                        <span className="block font-medium" style={{ color: 'var(--melio-charcoal)' }}>
                          {formatTime(slot)}
                        </span>
                        {slot.doctorName && (
                          <span className="block text-xs mt-0.5" style={{ color: 'var(--melio-text-muted)' }}>
                            with {slot.doctorName}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
    </PublicPageShell>
  );
};

export default ClinicalHistoryBookPage;
